import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Center,
  Container,
  Divider,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconBolt, IconBook2, IconCar, IconSearch } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import SearchConfig from "@components/SearchConfig";
import SearchHero from "@components/SearchHero";
import SettingRow from "@components/SettingRow";
import {
  DEFAULT_REPAIR_SETTINGS,
  RepairInformationResults,
  RepairInformationSettings,
  patchRepairSettings,
  useRepairInformationSearch,
  useVehicles,
  type RepairSettings,
} from "@features/repair-information";
import { TopicTreeResults, useTopicTreeSearch } from "@features/topic-tree";

const MIN_QUERY_LENGTH = 3;
/** The search service refuses longer queries (422, ``shared/limits.py``). */
const MAX_QUERY_LENGTH = 1000;

/** One source per capability of the search service, picked in the search settings. */
export const SEARCH_SOURCES = [
  {
    value: "technical-manual",
    label: "Technical manual",
    short: "Manual",
    placeholder: "Search the technical manual…",
    icon: IconBook2,
  },
  {
    value: "wiring-diagram",
    label: "Wiring diagram",
    short: "Wiring",
    placeholder: "Search the wiring diagrams…",
    icon: IconBolt,
  },
  {
    value: "repair-information",
    label: "Repair information",
    short: "Repair",
    placeholder: "Ask about this vehicle, e.g. “Zahnriemen wechseln”",
    icon: IconCar,
  },
] as const;

type SearchSource = (typeof SEARCH_SOURCES)[number]["value"];

function isSearchSource(value: string | null): value is SearchSource {
  return SEARCH_SOURCES.some((source) => source.value === value);
}

/** Everything the settings popover edits; applied as a whole. */
interface SearchSettings {
  source: SearchSource;
  topK: number;
  repair: RepairSettings;
}

const SearchPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  // `?tab=` is the parameter of the earlier tabbed page; old links keep working.
  const requested = params.get("source") ?? params.get("tab");
  const source: SearchSource = isSearchSource(requested) ? requested : "technical-manual";
  const [topK, setTopK] = useState(10);
  const [repairSettings, setRepairSettings] = useState<RepairSettings>(DEFAULT_REPAIR_SETTINGS);
  const applied: SearchSettings = { source, topK, repair: repairSettings };

  // The popover edits a draft; Apply commits it, Cancel drops it.
  const [draft, setDraft] = useState<SearchSettings | null>(null);
  const settingsOpen = draft !== null;
  const shown = draft ?? applied;

  const sourceInfo = SEARCH_SOURCES.find((s) => s.value === source) ?? SEARCH_SOURCES[0];
  const isRepair = source === "repair-information";
  const needsVehicle = isRepair && repairSettings.vehicle === null;
  const draftNeedsVehicle = shown.source === "repair-information" && shown.repair.vehicle === null;

  const vehicles = useVehicles(shown.source === "repair-information");
  const topicTree = useTopicTreeSearch();
  const repair = useRepairInformationSearch();
  const [query, setQuery] = useState("");

  // Results belong to one source: switching source clears them.
  const { reset: resetTopicTree } = topicTree;
  useEffect(() => resetTopicTree(), [source, resetTopicTree]);

  const openSettings = (): void => setDraft(applied);
  const cancelSettings = (): void => setDraft(null);
  const editDraft = (patch: Partial<SearchSettings>): void =>
    setDraft((current) => (current ? { ...current, ...patch } : current));
  const applySettings = (): void => {
    if (!draft || draftNeedsVehicle) return;
    if (draft.source !== source) setParams({ source: draft.source }, { replace: true });
    setTopK(draft.topK);
    setRepairSettings(draft.repair);
    setDraft(null);
  };

  const text = query.trim();
  const loading = isRepair ? repair.loading : topicTree.loading;

  const runSearch = (): void => {
    if (text.length < MIN_QUERY_LENGTH) return;
    if (needsVehicle) {
      openSettings();
      return;
    }
    setDraft(null);
    if (isRepair) void repair.run(text, repairSettings);
    else void topicTree.run(source, text, topK);
  };

  return (
    <Container size="xl" pb="4rem">
      <Stack gap="lg" align="center" justify="flex-start">
        <SearchHero />
        <Stack gap="md" w="100%" maw={1100}>
          <Paper withBorder radius="md" bg="white" p={6} w="100%">
            <Group gap="xs" wrap="nowrap">
              <SearchConfig
                opened={settingsOpen}
                onOpen={openSettings}
                onCancel={cancelSettings}
                onApply={applySettings}
                applyBlockedReason={draftNeedsVehicle ? "Pick a vehicle" : null}
                attention={needsVehicle}
              >
                <Stack gap={6}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    Search in
                  </Text>
                  <SegmentedControl
                    fullWidth
                    size="sm"
                    color="brand"
                    aria-label="Search in"
                    value={shown.source}
                    onChange={(value) => isSearchSource(value) && editDraft({ source: value })}
                    data={SEARCH_SOURCES.map(({ value, short, icon: Icon }) => ({
                      value,
                      label: (
                        <Center style={{ gap: 6 }}>
                          <Icon size={15} />
                          <span>{short}</span>
                        </Center>
                      ),
                    }))}
                  />
                </Stack>
                {shown.source === "repair-information" && (
                  <RepairInformationSettings
                    value={shown.repair}
                    onChange={(patch) => editDraft({ repair: patchRepairSettings(shown.repair, patch) })}
                    vehicles={vehicles}
                  />
                )}
                {shown.source !== "repair-information" && (
                  <>
                    <Divider />
                    <SettingRow label="Top K">
                      <NumberInput
                        size="xs"
                        w={80}
                        aria-label="Top K"
                        value={shown.topK}
                        onChange={(value) => typeof value === "number" && editDraft({ topK: value })}
                        min={1}
                        max={100}
                        clampBehavior="strict"
                      />
                    </SettingRow>
                  </>
                )}
              </SearchConfig>
              <TextInput
                flex={1}
                size="md"
                variant="unstyled"
                aria-label="Search query"
                placeholder={needsVehicle ? "Pick a vehicle in the settings first" : sourceInfo.placeholder}
                value={query}
                maxLength={MAX_QUERY_LENGTH}
                onChange={(event) => setQuery(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") runSearch();
                }}
              />
              <Button
                size="md"
                radius="sm"
                color="brand"
                leftSection={<IconSearch size={18} />}
                onClick={runSearch}
                loading={loading}
                disabled={text.length < MIN_QUERY_LENGTH}
              >
                Search
              </Button>
            </Group>
          </Paper>

          <Box w="100%">
            {isRepair ? <RepairInformationResults state={repair} /> : <TopicTreeResults state={topicTree} />}
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};

export default SearchPage;
